import { Fragment } from 'react';
import { FigureChip } from '@/components/ui/figure-chip';
import { cn } from '@/lib/utils/cn';
import { scenarioSteps, vendorCardParts } from './block-forms';
import { bulletItems, splitTokens, type TokenValues } from './tokens';
import type { BlockKind, EditorBlock } from './types';

/** Text with each `{{token}}` shown as its figure chip; the words as written. */
export function TokenText({
  text,
  tokens,
  source,
}: {
  text: string;
  tokens: TokenValues;
  source?: string;
}) {
  return (
    <>
      {splitTokens(text, tokens).map((p, i) =>
        p.kind === 'text' ? (
          <Fragment key={i}>{p.text}</Fragment>
        ) : (
          <FigureChip key={i} name={p.name} value={p.value} source={source} />
        ),
      )}
    </>
  );
}

/**
 * A block in reading form, by kind (E24-design §6): a paragraph; a list
 * with one item per line; a headline in the display face; a vendor card
 * (the rank and rank label, the name, the body, the Watch line, the two
 * figures by token); the scenarios as a numbered table with the RTM
 * numbers from each step's requirement citations. The React equivalent of
 * what `packages/pdf-render` prints for each kind.
 */
export function BlockBody({
  kind,
  text,
  tokens,
  source,
  className,
  block,
}: {
  kind: BlockKind;
  text: string;
  tokens: TokenValues;
  source?: string;
  className?: string;
  /** The block itself, for the card's code-composed parts and the citations a step carries. */
  block?: Pick<EditorBlock, 'card' | 'citations'>;
}) {
  if (text.trim().length === 0) {
    return <p className={cn('m-0 italic text-ink-500', className)}>Empty. Edit it to write it.</p>;
  }
  if (kind === 'bullets') {
    return (
      <ul className={cn('m-0 list-disc space-y-1 pl-5 text-body leading-relaxed', className)}>
        {bulletItems(text).map((item, i) => (
          <li key={i}>
            <TokenText text={item} tokens={tokens} source={source} />
          </li>
        ))}
      </ul>
    );
  }
  if (kind === 'headline') {
    return (
      <h2
        className={cn('m-0 font-serif text-h1 font-semibold leading-tight text-ink-900', className)}
      >
        <TokenText text={text} tokens={tokens} source={source} />
      </h2>
    );
  }
  if (kind === 'vendor_card') {
    const parts = vendorCardParts(text);
    const rank = block?.card?.rank ?? null;
    return (
      <div className={cn('flex min-w-0 flex-col gap-1.5', className)} data-block-kind="vendor_card">
        {parts.rankLabel ? (
          <p className="m-0 text-meta font-semibold uppercase tracking-wider text-ink-500">
            {rank !== null ? `${rank} · ` : ''}
            <TokenText text={parts.rankLabel} tokens={tokens} source={source} />
          </p>
        ) : null}
        {block?.card ? (
          <p className="m-0 font-serif text-h2 font-semibold text-ink-900">{block.card.name}</p>
        ) : null}
        {parts.body ? (
          <p className="m-0 whitespace-pre-wrap text-body-sm leading-relaxed text-ink-700">
            <TokenText text={parts.body} tokens={tokens} source={source} />
          </p>
        ) : null}
        {parts.watch ? (
          <p className="m-0 text-body-sm italic leading-relaxed text-ink-700">
            Watch: <TokenText text={parts.watch} tokens={tokens} source={source} />
          </p>
        ) : null}
        {block?.card && block.card.figures.length > 0 ? (
          <dl className="m-0 mt-auto flex flex-wrap gap-x-4 gap-y-1 pt-1 text-meta text-ink-500">
            {block.card.figures.map((f) => (
              <div key={f.token} className="flex items-baseline gap-1.5">
                <dt className="uppercase tracking-wider">{f.label}</dt>
                <dd className="m-0 text-body-sm">
                  <FigureChip name={f.token} value={tokens[f.token] ?? null} source={source} />
                </dd>
              </div>
            ))}
          </dl>
        ) : null}
      </div>
    );
  }
  if (kind === 'scenarios') {
    const steps = scenarioSteps(text, block?.citations ?? []);
    return (
      <div className={cn('overflow-x-auto rounded-card border border-ink-100', className)}>
        <table className="w-full border-collapse text-body-sm" aria-label="Scripted scenarios">
          <thead>
            <tr className="border-b border-ink-100 text-left text-meta font-medium text-ink-500">
              <th scope="col" className="w-8 px-3 py-2 text-right font-medium">
                #
              </th>
              <th scope="col" className="px-3 py-2 font-medium">
                Scripted scenario
              </th>
              <th scope="col" className="whitespace-nowrap px-3 py-2 font-medium">
                RTM
              </th>
            </tr>
          </thead>
          <tbody>
            {steps.map((s, i) => (
              <tr key={i} className="border-b border-ink-100 align-top last:border-b-0">
                <td className="px-3 py-2 text-right font-mono tabular-nums text-ink-500">
                  {i + 1}
                </td>
                <td className="px-3 py-2 text-ink-900">
                  <TokenText text={s.text} tokens={tokens} source={source} />
                  {s.cites.length > 0 ? (
                    <span className="ml-1.5 font-mono text-mono-sm text-ink-500">
                      {s.cites.map((c) => c.label).join(', ')}
                    </span>
                  ) : null}
                </td>
                <td className="whitespace-nowrap px-3 py-2 font-mono tabular-nums text-ink-700">
                  {s.rtm.length > 0 ? s.rtm.join(', ') : '–'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  return (
    <p className={cn('m-0 whitespace-pre-wrap text-body leading-relaxed', className)}>
      <TokenText text={text} tokens={tokens} source={source} />
    </p>
  );
}
